import axios from 'axios';
import { clearMasterToken, limparSessaoLocal } from './utils/auth';
import { limparInscricaoPushLocal } from './utils/serviceWorker';
import { codigoDeErroApi, mensagemDeErroApi, statusDeErroApi } from './utils/erroApi';

const getBaseURL = () => {
  if (process.env.NODE_ENV === 'production') {
    return window.location.origin + '/api';
  }
  return 'http://localhost:5000/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 120000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to clear all auth data
const clearAuthData = () => {
  // Token inválido: cancela a inscrição local para o próximo usuário do
  // dispositivo não receber notificações da conta anterior.
  limparInscricaoPushLocal().catch(() => {});
  limparSessaoLocal();
  window.location.href = '/';
};

// Adicionar token JWT a cada requisição.
// Prioridade: 1) header explícito na requisição, 2) master token ativo, 3) token do localStorage.
api.interceptors.request.use((req) => {
  if (!req.headers.Authorization) {
    // Verifica se há um token mestre ativo no sessionStorage
    const masterToken = sessionStorage.getItem('masterToken');
    if (masterToken) {
      try {
        const payload = JSON.parse(atob(masterToken.split('.')[1]));
        const isValid = payload.isMasterAccess && (!payload.exp || Date.now() / 1000 < payload.exp);
        if (isValid) {
          req.headers.Authorization = `Bearer ${masterToken}`;
          return req;
        } else {
          // Token mestre expirado — limpa automaticamente (e os tokens de
          // preview, que foram emitidos para essa perspectiva).
          clearMasterToken();
        }
      } catch {
        clearMasterToken();
      }
    }

    // Sem master token — usa o token regular do admin/cliente
    const token = localStorage.getItem('token');
    if (token) {
      req.headers.Authorization = `Bearer ${token}`;
    }
  }
  return req;
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Normaliza o erro da API uma única vez: as telas usam `mensagemApi`
    // (contrato `{ error, code, details }` do backend) em vez de escolher
    // entre `error`, `message` ou `mensagem`.
    error.mensagemApi = mensagemDeErroApi(error);
    error.codigoApi = codigoDeErroApi(error);
    error.statusApi = statusDeErroApi(error);

    if (error.response) {
      // Server responded with error status
      if (error.response.status === 503) {
        // Database connection error
        console.error('Database connection error:', error.response.data);
      } else if (error.response.status === 401) {
        // Só faz logout se o erro foi causado pelo token principal (localStorage),
        // não pelo token mestre (sessionStorage). Se a requisição usou um token
        // diferente do token do admin, apenas limpa o token mestre.
        const adminToken = localStorage.getItem('token');
        const requestAuth = error.config?.headers?.Authorization;
        const usedAdminToken = requestAuth === `Bearer ${adminToken}`;

        if (usedAdminToken) {
          clearAuthData();
        } else {
          // Token mestre expirado — remove o token mestre e os tokens de
          // preview emitidos para ele.
          clearMasterToken();
        }
      }
    } else if (error.request) {
      // Request made but no response received
      console.error('No response received:', error.request);
    } else {
      // Error in request setup
      console.error('Request setup error:', error.message);
    }
    return Promise.reject(error);
  }
);

export default api;
